// components/PhotoLightbox.js
// Full-screen photo/video viewer with navigation
import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiChevronLeft, FiChevronRight, FiDownload } from 'react-icons/fi';
import { getPresignedVideoUrl, downloadFile } from '../services/albumService';

const PhotoLightbox = ({ visible, photos, initialIndex = 0, onClose, albumName }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [downloading, setDownloading] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);

  const currentPhoto = photos[currentIndex];
  const isVideo = currentPhoto?.media_type === 'video';

  useEffect(() => {
    if (visible) {
      setCurrentIndex(initialIndex);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [visible, initialIndex]);

  // Load video URL when switching to a video item
  useEffect(() => {
    if (isVideo && currentPhoto?.id) {
      loadVideoUrl(currentPhoto.id);
    } else {
      setVideoUrl(null);
    }
  }, [currentIndex, isVideo, currentPhoto?.id]);

  const loadVideoUrl = async (mediaId) => {
    setVideoLoading(true);
    setVideoUrl(null);
    try {
      const result = await getPresignedVideoUrl(mediaId);
      if (result.success) {
        setVideoUrl(result.url);
      }
    } catch (err) {
      console.error('Error loading video URL:', err);
    } finally {
      setVideoLoading(false);
    }
  };

  const goNext = useCallback(() => {
    if (currentIndex < photos.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, photos.length]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowRight') goNext();
    if (e.key === 'ArrowLeft') goPrev();
  }, [onClose, goNext, goPrev]);

  useEffect(() => {
    if (visible) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [visible, handleKeyDown]);

  const handleDownload = async () => {
    if (!currentPhoto) return;
    setDownloading(true);
    try {
      const url = isVideo && videoUrl ? videoUrl : currentPhoto.cdnUrl;
      const ext = isVideo ? 'mp4' : 'jpg';
      const filename = `${albumName || 'album'}_${currentIndex + 1}.${ext}`;
      await downloadFile(url, filename);
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloading(false);
    }
  };

  if (!visible || !photos.length) return null;

  return (
    <div className="photo-lightbox-overlay">
      {/* Top bar */}
      <div className="lightbox-top-bar">
        <button className="lightbox-close" onClick={onClose}>
          <FiX />
        </button>
        <div className="lightbox-counter">
          {currentIndex + 1} / {photos.length}
        </div>
        <div className="lightbox-actions">
          {/* Placeholder for symmetry */}
          <div style={{ width: 44 }} />
        </div>
      </div>

      {/* Content */}
      <div className="lightbox-content">
        {isVideo ? (
          videoLoading ? (
            <div className="lightbox-video-loading">
              <div className="spinner" />
              <span>Loading video...</span>
            </div>
          ) : videoUrl ? (
            <video
              className="lightbox-video"
              src={videoUrl}
              controls
              autoPlay
              playsInline
            />
          ) : (
            <div className="lightbox-video-loading">
              <span>Failed to load video</span>
            </div>
          )
        ) : (
          <img
            className="lightbox-image"
            src={currentPhoto.cdnUrl}
            alt={`${currentIndex + 1} of ${photos.length}`}
            draggable={false}
          />
        )}

        {/* Navigation arrows */}
        {currentIndex > 0 && (
          <button className="lightbox-nav prev" onClick={goPrev}>
            <FiChevronLeft />
          </button>
        )}
        {currentIndex < photos.length - 1 && (
          <button className="lightbox-nav next" onClick={goNext}>
            <FiChevronRight />
          </button>
        )}
      </div>

      {/* Bottom bar */}
      <div className="lightbox-bottom-bar">
        <button
          className="lightbox-download-btn"
          onClick={handleDownload}
          disabled={downloading}
        >
          {downloading ? (
            <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} />
          ) : (
            <FiDownload />
          )}
        </button>
      </div>
    </div>
  );
};

export default PhotoLightbox;
